import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CommitProgress from "../components/CommitProgress";
import ManufacturingStatusPanel from "./ManufacturingStatusPanel";
import ManufacturingToolbar from "../toolbars/ManufacturingToolbar";
import Paper from "@mui/material/Paper";
import React, { useCallback, useEffect, useState } from "react";
import Stack from "@mui/material/Stack";
import StatusDialog from "../components/StatusDialog";
import Typography from "@mui/material/Typography";
import { createAxiosClient } from "@ntg/component-library/utilities";
import { useGetCItemStatusList } from "../hooks/Manufacturing/useGetCItemStatusList";
import { useGetManufacturingItemInit } from "../hooks/Manufacturing/useGetManufacturingItemInit";
import { useLayoutContext } from "@ntg/component-library/contexts";
import { useLocation, useNavigate, useParams } from "react-router";

const apiClient = createAxiosClient("D365FO");

/**
 * The D365 commit pipeline steps in cascade order.
 * Each step maps to a FunctionAppsStarter endpoint.
 * Commit: execute from clicked step forward.
 * Rollback: execute from the end backward to clicked step.
 */
const COMMIT_CHAIN = [
    { statusTypeID: 14, endpoint: "CommitProductsToManufacturing", needsDesignID: true },
    { statusTypeID: 15, endpoint: "CommitReleasedProductsToManufacturing", needsDesignID: true },
    { statusTypeID: 16, endpoint: "CommitRouteToManufacturing", needsDesignID: true },
    { statusTypeID: 17, endpoint: "CommitBomToManufacturing", needsDesignID: true },
    { statusTypeID: 10, endpoint: "CommitMfgSalesOrderLines", needsDesignID: false },
];

export default function ManufacturingDetailContainer() {
    const { id } = useParams<{ id: string }>();
    const { SetPrimaryTitle, SetSecondaryHeader } = useLayoutContext();
    const navigate = useNavigate();
    const location = useLocation();
    const orderItemID = Number(id);
    const [isCommitting, setIsCommitting] = useState(false);

    const { data: init, isLoading: initLoading } =
        useGetManufacturingItemInit(orderItemID);

    const {
        data: statuses,
        isLoading: statusLoading,
        refetch: refetchStatuses,
    } = useGetCItemStatusList(init?.mItemID ?? 0);

    useEffect(() => {
        SetPrimaryTitle(
            initLoading
                ? "Loading..."
                : `Manufacturing ${init?.coOrderItemID ?? ""}`,
        );
        SetSecondaryHeader(
            <ManufacturingToolbar
                DesignID={init?.designID ?? 0}
                OrderItemID={orderItemID}
                Statuses={statuses ?? []}
            />,
        );

        return () => {
            SetPrimaryTitle("");
            SetSecondaryHeader(null);
        };
    }, [
        SetPrimaryTitle,
        SetSecondaryHeader,
        initLoading,
        init,
        orderItemID,
        statuses,
    ]);

    const handleCommit = useCallback(
        async (statusTypeID: number) => {
            if (!init || isCommitting) return;

            const startIndex = COMMIT_CHAIN.findIndex(
                (s) => s.statusTypeID === statusTypeID,
            );
            if (startIndex === -1) return;

            const stepsToRun = COMMIT_CHAIN.slice(startIndex);

            const confirmed = window.confirm(
                `Commit ${stepsToRun.length} step(s) starting from this point?`,
            );
            if (!confirmed) return;

            setIsCommitting(true);
            try {
                let lastInstanceId = "";
                for (const step of stepsToRun) {
                    const payload = step.needsDesignID
                        ? { designID: init.designID, mItemID: init.mItemID }
                        : init.mItemID;
                    const headers = step.needsDesignID
                        ? {}
                        : { "Content-Type": "application/json" };
                    const response = await apiClient.post<string>(
                        `/FunctionAppsStarter/${step.endpoint}`,
                        payload,
                        { headers },
                    );
                    lastInstanceId = response.data;
                }
                if (lastInstanceId) {
                    navigate(`${location.pathname}/commit/${lastInstanceId}`);
                }
                await refetchStatuses();
            } catch (error) {
                console.error("Commit chain failed:", error);
                alert("Commit failed. Check console for details.");
            } finally {
                setIsCommitting(false);
            }
        },
        [init, isCommitting, navigate, location.pathname, refetchStatuses],
    );

    const handleRollback = useCallback(
        async (statusTypeID: number) => {
            if (!init || isCommitting) return;

            const targetIndex = COMMIT_CHAIN.findIndex(
                (s) => s.statusTypeID === statusTypeID,
            );
            if (targetIndex === -1) return;

            // Rollback from end back to target (reverse order)
            const stepsToRun = COMMIT_CHAIN.slice(targetIndex).reverse();

            const confirmed = window.confirm(
                `Roll back ${stepsToRun.length} step(s) to this point?`,
            );
            if (!confirmed) return;

            setIsCommitting(true);
            try {
                for (const step of stepsToRun) {
                    const payload = step.needsDesignID
                        ? { designID: init.designID, mItemID: init.mItemID }
                        : init.mItemID;
                    const headers = step.needsDesignID
                        ? {}
                        : { "Content-Type": "application/json" };
                    await apiClient.post<string>(
                        `/FunctionAppsStarter/${step.endpoint}`,
                        payload,
                        { headers },
                    );
                }
                await refetchStatuses();
            } catch (error) {
                console.error("Rollback chain failed:", error);
                alert("Rollback failed. Check console for details.");
            } finally {
                setIsCommitting(false);
            }
        },
        [init, isCommitting, refetchStatuses],
    );

    if (initLoading || statusLoading) return <p>Loading...</p>;

    return (
        <Stack spacing={2}>
            <Card component={Paper} sx={{ padding: 1 }}>
                <CardContent
                    sx={{
                        display: "flex",
                        alignContent: "center",
                        gap: 2,
                        flexDirection: "column",
                    }}
                >
                    <Stack
                        direction="row"
                        sx={{
                            justifyContent: "space-between",
                            gap: 6,
                        }}
                    >
                        <Typography variant="h5">
                            {init?.itemDescription}
                        </Typography>
                        <Typography variant="h5">
                            Contact: {init?.contact}
                        </Typography>
                        <Typography variant="h5">
                            Boat: {init?.boat}
                        </Typography>
                        <Typography variant="h5">
                            D365 Company: {init?.dataAreaId}
                        </Typography>
                    </Stack>
                </CardContent>
            </Card>

            <Card component={Paper} sx={{ padding: 1 }}>
                <CardContent>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                        Manufacturing Status
                    </Typography>
                    <ManufacturingStatusPanel
                        isCommitting={isCommitting}
                        onCommit={handleCommit}
                        onRollback={handleRollback}
                        statuses={statuses ?? []}
                    />
                </CardContent>
            </Card>

            <StatusDialog title="Commit Progress" expectedPaths={["commit"]}>
                <CommitProgress />
            </StatusDialog>
        </Stack>
    );
}
